import { z } from 'zod';
import { actionMethods, assertionMethods } from '~/ai_tools/playwright_utils/types';
import { zCreateOperationParentContext, zValueType } from '~/server/logic/steps/operation/types';
import { zPosition } from '../recorder/recorder-operation';
import { TestStepMode } from '@prisma/client';

export const LOG_TYPES = ['log', 'debug', 'info', 'error', 'warning'] as const;
export type LogType = (typeof LOG_TYPES)[number];
export const isLogType = (type: string): type is LogType => LOG_TYPES.includes(type as LogType);

// Common Zod enums
export const zInitiator = z.enum(['executor', 'recorder']);
export const zStepStatus = z.enum(['begin', 'finished']);

export const zExecutorVar = z.object({
  name: z.string(),
  // We may not know the value yet (ex: email extractions) but we do know the playwright TypeScript types
  // (e.g. 'string', 'number', 'boolean')
  value: z.any().optional(),
  valueType: zValueType,
  playwrightType: z.string(),
  // Whether this variable contains sensitive data (e.g., passwords, API keys)
  // Sensitive variables will have their values redacted in AI prompts
  sensitive: z.boolean().optional(),
});
export type ExecutorVar = z.infer<typeof zExecutorVar>;

export const zStepInfo = z.object({
  id: z.string(),
  order: z.number(),
  description: z.string(),
  sourceOperationId: z.string().optional(),
  mode: z.enum(TestStepMode),
});

const zOperationInfo = z.object({
  id: z.string(),
  order: z.number().optional(),
});

// In the front-end, this is also sometimes called 'replay' or 'rewind'. It can happen during preview or record mode.
export const zFastForwardMessage = z.object({
  type: z.literal('fast-forward'),
  aliasesToUrls: z.record(z.string(), z.string()),
  // Target step to fast-forward to
  step: zStepInfo,
  // Target operation to fast-forward to
  operationId: z.string().optional(),
  variables: zExecutorVar.array(),
});

const _zCompletedItemsUpdateAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('append-operation'),
    operationId: z.string(),
  }),
  z.object({
    type: z.literal('append-step'),
    stepId: z.string(),
  }),
  z.object({
    type: z.literal('reset'),
  }),
]);
export type CompletedItemsUpdateAction = z.infer<typeof _zCompletedItemsUpdateAction>;

const zCompletedItemsUpdate = z.object({
  type: z.literal('completed-items-update'),
  update: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('append-operation'),
      operationId: z.string(),
    }),
    z.object({
      type: z.literal('append-step'),
      stepId: z.string(),
    }),
    z.object({
      type: z.literal('reset'),
    }),
  ]),
});

const zTimeoutUpdate = z.object({
  type: z.literal('timeout-update'),
  update: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('start'),
      stepId: z.string(),
      totalTimeoutMs: z.number(),
    }),
    z.object({
      type: z.literal('hide'),
      stepId: z.string(),
    }),
  ]),
});

export const zTabUpdateMessage = z.object({
  type: z.literal('tab-update'),
  updateType: z.enum(['opened', 'closed', 'switch']),
  pageAlias: z.string(),
  options: z.object({ dontSwitchToPage: z.boolean().optional() }).optional(),
});
export type TabUpdateMessage = z.output<typeof zTabUpdateMessage>;

export const zSelectOptions = z.object({
  type: z.literal('select-options'),
  options: z
    .object({
      value: z.string(),
      label: z.string(),
      disabled: z.boolean(),
      selected: z.boolean(),
    })
    .array()
    .min(1),
  target: zPosition,
});
export type VmSelectOptionsPayload = Omit<z.output<typeof zSelectOptions>, 'type'>;

export const zFillInput = z.object({
  type: z.literal('fill-input'),
  data: z.object({
    value: z.string(),
    tag: z.string(),
    type: z.string(),
    placeholder: z.string(),
  }),
  target: zPosition,
});
export type VmFillInputPayload = Omit<z.output<typeof zFillInput>, 'type'>;

export const zRecordAction = z.object({
  type: z.literal('record-action'),
  action: z.enum(actionMethods),
  target: zPosition.optional(),
  value: z.string().optional(),
});

export const zNetworkLog = z.object({
  type: z.literal('network-log'),
  requestId: z.string(),
  url: z.string(),
  method: z.string(),
  startTime: z.number(),
  status: z.object({
    code: z.number(),
    text: z.string(),
  }),
  duration: z.number(),
  size: z.number(),
  requestHeaders: z.record(z.string(), z.string()),
  responseHeaders: z.record(z.string(), z.string()),
  responseBody: z.string().optional(),
});
export type NetworkLog = z.infer<typeof zNetworkLog>;

const zAIChainOfThoughtMessage = z.object({
  type: z.literal('chain-of-thought'),
  content: z.string(),
});

const zAIKnowledgeRetrievalMessage = z.object({
  type: z.literal('knowledge-retrieval'),
  knowledgeItems: z.array(z.string()),
});

const zAIObservationMessage = z.object({
  type: z.literal('observation'),
  content: z.string(),
});

const zAIDecisionMessage = z.object({
  type: z.literal('decision'),
  content: z.string(),
});

const zAIActionMessage = z.object({
  type: z.literal('action'),
  content: z.string().optional(),
  actionType: z.enum(actionMethods),
  target: z.string().optional(),
});

const zAIDefineVariableMessage = z.object({
  type: z.literal('define-variable'),
  variableType: z.enum(['STATIC', 'DYNAMIC']),
  variableName: z.string(),
  variableValue: z.string(),
});

const zAIAssertionMessage = z.object({
  type: z.literal('assertion'),
  assertionType: z.enum(assertionMethods),
  target: z.string(),
  value: z.string().optional(),
});

const zAICompletionMessage = z.object({
  type: z.literal('completion'),
  content: z.string(),
  success: z.boolean(),
});

const zAIComponentCallMessage = z.object({
  type: z.literal('component-call'),
  componentName: z.string(),
  inputParams: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
});

const zAIStepStatusMessage = z.object({
  type: z.literal('ai-generation-status'),
  status: zStepStatus,
  prompt: z.string(),
  generationReason: z.enum(['step', 'action', 'autoheal']),
  stepId: z.string(),
  executionId: z.string(),
  initiator: zInitiator,
  operationId: z.string().optional(),
  epochMs: z.number(),
});

const zAutohealStepStatusMessage = z.object({
  type: z.literal('autoheal-step-status'),
  status: zStepStatus,
  prompt: z.string(),
  stepId: z.string(),
  executionId: z.string(),
  initiator: zInitiator,
});

const zAIStatusMessage = z.object({
  type: z.literal('status'),
  status: z.enum(['success', 'error', 'warning', 'info', 'generating', 'cancelled']),
  content: z.string(),
});

const zAITipMessage = z.object({
  type: z.literal('tip'),
  content: z.string(),
  category: z.enum(['latency', 'accuracy', 'maintainability', 'other']).optional(),
});

const zAIExtractionStartMessage = z.object({
  type: z.literal('ai-extraction-start'),
  variableName: z.string(),
  extractionPrompt: z.string(),
});

const zAIExtractionResultMessage = z.discriminatedUnion('success', [
  z.object({
    type: z.literal('ai-extraction-result'),
    success: z.literal(true),
    variableName: z.string(),
    extractedValue: z.string(),
  }),
  z.object({
    type: z.literal('ai-extraction-result'),
    success: z.literal(false),
    variableName: z.string(),
  }),
]);

const zAIVisualAssertionResultMessage = z.object({
  type: z.literal('ai-assertion-result'),
  isCorrect: z.boolean(),
});

const zAIVisualAssertionStartMessage = z.object({
  type: z.literal('ai-assertion-start'),
  content: z.string(),
});

const zAIKnowledgeSuggestionMessage = z.discriminatedUnion('actionId', [
  z.object({
    type: z.literal('knowledge-suggestion'),
    content: z.string(),
    actionId: z.enum(['save-knowledge']),
    payload: z.object({
      knowledgeItems: z.array(z.string()).min(1),
    }),
  }),
]);

const zAIScreenshotAnalysisMessage = z.object({
  type: z.literal('screenshot-analysis'),
  base64Screenshot: z.string(),
});

const zAIConditionalBlockResultMessage = z.object({
  type: z.literal('conditional-evaluation-result'),
  resolvedTo: z.boolean(),
});

const zAIDialogMessage = z.object({
  type: z.literal('dialog'),
  dialogType: z.enum(['alert', 'confirm', 'prompt', 'beforeunload']),
  message: z.string(),
  response: z.string().optional(),
});
export type AIDialogMessage = z.infer<typeof zAIDialogMessage>;

export const zAIMessage = z
  .discriminatedUnion('type', [
    zAIChainOfThoughtMessage,
    zAIKnowledgeRetrievalMessage,
    zAIObservationMessage,
    zAIDecisionMessage,
    zAIActionMessage,
    zAIDefineVariableMessage,
    zAIAssertionMessage,
    zAICompletionMessage,
    zAIComponentCallMessage,
    zAIStatusMessage,
    zAIStepStatusMessage,
    zAutohealStepStatusMessage,
    zAITipMessage,
    zAIExtractionStartMessage,
    zAIVisualAssertionResultMessage,
    zAIVisualAssertionStartMessage,
    zAIExtractionResultMessage,
    zAIKnowledgeSuggestionMessage,
    zAIScreenshotAnalysisMessage,
    zAIConditionalBlockResultMessage,
    zAIDialogMessage,
  ])
  .and(
    z.object({
      testCaseId: z.string().optional(),
      videoTimestampSeconds: z.number().optional(),
    }),
  );

export type AIMessage = z.infer<typeof zAIMessage>;

export const zVmServerToClientMessage = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('log'),
    msg: z.string(),
  }),
  z.object({
    type: z.literal('browser-log'),
    msg: z.string(),
    logType: z.enum(LOG_TYPES),
  }),
  zCompletedItemsUpdate,
  zNetworkLog,
  z.object({
    type: z.literal('ai-message'),
    data: zAIMessage,
  }),
  zSelectOptions,
  zFillInput,
  zRecordAction,
  z.object({
    type: z.literal('preview-success'),
    stepId: z.string(),
    stepOrder: z.number(),
    operation: z
      .object({
        id: z.string(),
        order: z.number(),
      })
      .optional(),
    resumeFromCursor: z.boolean(),
    variables: zExecutorVar.array(),
  }),
  zFastForwardMessage,
  z.object({
    type: z.literal('execution-error'),
    aliasesToUrls: z.record(z.string(), z.string()),
    step: zStepInfo,
    operation: zOperationInfo,
    error: z.string(),
    playwrightCode: z.string(),
    aborted: z.boolean(),
    resumeFromCursor: z.boolean(),
    variables: zExecutorVar.array(),
  }),
  z.object({
    type: z.literal('recording'),
    aliasesToUrls: z.record(z.string(), z.string()),
  }),
  z.object({
    type: z.literal('recorded'),
    aliasesToUrls: z.record(z.string(), z.string()),
    variables: zExecutorVar.array(),
    recorderCursor: z.object({
      parentContext: zCreateOperationParentContext.optional(),
      stepId: z.string(),
      stepOrder: z.number(),
      operation: z
        .object({
          id: z.string(),
          order: z.number(),
        })
        .optional(),
    }),
  }),
  z.object({
    type: z.literal('prompt'),
    dialogMessage: z.string(),
  }),
  // Usually means the VM is done replaying previous steps and is waiting for user input
  z.object({
    type: z.enum(['waiting-user-input']),
    message: z.string().optional(),
    aliasesToUrls: z.record(z.string(), z.string()),
    variables: zExecutorVar.array(),
  }),
  z.object({
    type: z.enum(['warning']),
    message: z.string(),
    aliasesToUrls: z.record(z.string(), z.string()),
  }),
  z.object({ type: z.literal('refresh-code') }),
  z.object({
    type: z.literal('tab-syncing-urls'),
    aliasesToUrls: z.record(z.string(), z.string()),
  }),
  zTabUpdateMessage,
  z.object({
    type: z.literal('file-upload'),
    isMultiple: z.boolean(),
  }),
  z.object({
    type: z.literal('cursor-update'),
    recorderCursor: z.object({
      stepId: z.string(),
      stepOrder: z.number(),
      parentContext: zCreateOperationParentContext.optional(),
      operation: z
        .object({
          id: z.string(),
          order: z.number(),
        })
        .optional(),
    }),
  }),
  z.object({
    type: z.literal('conditional-evaluation-result'),
    conditionalBlockId: z.string(),
    resolvedTo: z.boolean(),
  }),
  z.object({
    type: z.literal('recorder-pause-change'),
    paused: z.boolean(),
  }),
  // Used to switch between record and preview mode
  z.object({
    type: z.literal('execution-type-change'),
    executionType: z.enum(['record', 'preview']),
    aliasesToUrls: z.record(z.string(), z.string()),
    variables: zExecutorVar.array(),
    newRecordCursor: z.object({
      parentContext: zCreateOperationParentContext.optional(),
      stepId: z.string(),
      stepOrder: z.number(),
      operation: z
        .object({
          id: z.string(),
          order: z.number(),
        })
        .optional(),
    }),
  }),
  z.object({
    type: z.literal('autoheal-step'),
    // Sometimes stepId is undefined and that means we need to reset the autoheal step
    stepId: z.string().optional(),
  }),
  z.object({
    type: z.literal('ai-fast-auto-heal'),
    stepId: z.string(),
    operationId: z.string(),
  }),
  zTimeoutUpdate,
  z.object({
    type: z.literal('preview-execution-started'),
    previewExecutionId: z.string(),
  }),
  z.object({
    type: z.literal('ai-prev-step-stored-in-temp'),
    stepId: z.string(),
    previewExecutionId: z.string(),
  }),
  z.object({
    type: z.literal('ai-step-started'),
    stepId: z.string(),
    previewExecutionId: z.string(),
  }),
]);

export type VmServerToClientMessage = z.infer<typeof zVmServerToClientMessage>;
